import React, { useState, useMemo } from 'react'
import DatePicker from 'app/theme/date-picker/DatePicker'
import Select from 'app/theme/forms/Select'
import Input from 'app/theme/forms/Input'
import TimePicker from 'app/theme/date-picker/TimePicker'
import LeadError from '../LeadError'
import { add as addTime, set as setTime } from 'date-fns'
import styles from './LeadDate.module.css'

const mergeTime = (day, time) => setTime(day, {
  hours: time.getHours(),
  minutes: time.getMinutes(),
  seconds: 0,
  milliseconds: 0,
})

export default function LeadDate(props) {
  const { data, setData, errors, setErrors } = props

  const startTime = setTime(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
  const endTime = setTime(new Date(), { hours: 23, minutes: 59, seconds: 59, milliseconds: 0 })

  const disableStart = useMemo(() => {
    return startTime
  }, [])

  const disableEnd = useMemo(() => {
    return data.start_date
      ? (data.multiday ? addTime(data.start_date, { days: 1 }) : data.start_date)
      : startTime
  }, [data.start_date, data.multiday])

  const showForVendingLead = data.payer === 'attendee'

  return (
    <div>
      { !data.recurring && <Description /> }
      <div className="p-8 grid gap-5 sm:gap-8">
        {data.recurring && (
          <div className="grid gap-3">
            <div>
              <label className="block font-light text-lg mb-2">
                How often would you like food service at your location?
              </label>
              <Select
                placeholder="Choose Option"
                value={data.frequency}
                options={[
                  { label: 'Daily', value: 'daily' },
                  { label: 'Weekly', value: 'weekly' },
                  { label: 'Monthly', value: 'monthly' },
                  { label: 'Yearly', value: 'yearly' },
                  { label: 'Other', value: 'other' },
                ]}
                onChange={frequency => {
                  setData(prev => ({
                    ...prev,
                    frequency,
                  }))
                  setErrors(prev => ({
                    ...prev,
                    frequency: null,
                  }))
                }}
              />
              {errors?.frequency && (
                <LeadError message={errors.frequency} />
              )}
            </div>
            {data.frequency === 'other' && (
              <div>
                <Input
                  placeholder="Additional details"
                  value={data.frequency_other}
                  onChange={frequency_other => {
                    setData(prev => ({
                      ...prev,
                      frequency_other,
                    }))
                  }}
                />
              </div>
            )}
          </div>
        )}

        <div>
          <div className="grid gap-5 sm:grid-cols-3 sm:gap-8 items-end">
            <div className={data.recurring ? 'sm:col-span-3' : 'sm:col-span-2'}>
              {!data.multiday ? (
                <div>
                  <label className="block font-light text-lg mb-2">
                    Start Date (optional)
                  </label>
                  <DatePicker
                    autoFocus
                    icon={false}
                    data-test-id='start_date'
                    value={data.start_date}
                    disableBefore={disableStart}
                    classNames={{
                      input: 'input-underline'
                    }}
                    onChange={date => {
                      let start_date = mergeTime(date, startTime)
                      let end_date = data.end_date || start_date

                      if (endTime.getHours() < 12) {
                        end_date = mergeTime(addTime(date, { days: 1 }), endTime)
                      } else {
                        end_date = mergeTime(date, endTime)
                      }

                      setData(prev => ({
                        ...prev,
                        start_date,
                        end_date,
                      }))

                      setErrors(prev => ({
                        ...prev,
                        start_date: null,
                        end_date: null,
                      }))
                    }}
                  />
                </div>
              ) : (
                <div>
                  <div className="flex items-center">
                    <div className='flex-1'>
                      <label className="block font-light text-lg mb-2">
                        Start Date (optional)
                      </label>
                      <div>
                        <DatePicker
                          icon={false}
                          value={data.start_date}
                          disableBefore={disableStart}
                          classNames={{
                            input: 'input-underline'
                          }}
                          onChange={date => {
                            let start_date = mergeTime(date, startTime)

                            setData(prev => ({
                              ...prev,
                              start_date,
                            }))
                            setErrors(prev => ({
                              ...prev,
                              start_date: null,
                              end_date: null,
                            }))
                          }}
                        />
                      </div>
                    </div>
                    <div className="text-xl font-light text-gray-600 px-5">
                      to
                    </div>
                    <div className={['flex-1', styles.toDatePicker]}>
                      <label className="block font-light text-lg mb-2">
                        End Date (optional)
                      </label>
                      <div>
                        <DatePicker
                          icon={false}
                          value={data.end_date}
                          disableBefore={disableEnd}
                          classNames={{
                            div: 'flex-1',
                            input: 'input-underline'
                          }}
                          onChange={date => {
                            setData(prev => ({
                              ...prev,
                              end_date: mergeTime(date, endTime),
                            }))
                            setErrors(prev => ({
                              ...prev,
                              start_date: null,
                              end_date: null,
                            }))
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {!data.recurring && (
              <div className="col-span-1">
                <button
                  children={data.multiday ? 'Single Day?' : 'Multiple Days?'}
                  className="btn btn-gray w-full px-5 py-2"
                  onClick={() => {
                    let start_date = data.start_date
                    let end_date = data.end_date

                    if (!start_date) {
                      end_date = undefined
                    } else if (endTime.getHours() >= 12) {
                      end_date = data.multiday ? start_date : addTime(start_date, { days: 1 })
                      end_date = mergeTime(end_date, endTime)
                    }

                    let dates = {
                      start_date,
                      end_date: null
                    }

                    if (data.multiday) {
                      dates = {
                        ...dates,
                        end_date
                      }
                    }

                    setData(prev => ({
                      ...prev,
                      ...dates,
                      multiday: !data.multiday,
                    }))
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {
          showForVendingLead &&
          <div>
            <div className="grid gap-5 sm:grid-cols-2 sm:gap-10">
              <div className="max-w-sm">
                <label className="block font-light text-lg mb-1">
                  Start Time
                </label>
                <TimePicker
                  className="text-sm"
                  date={startTime}
                  onChange={time => {
                    setStartTime(time)

                    setErrors(prev => ({
                      ...prev,
                      start_time: null,
                    }))

                    if (data.start_date) {
                      setData(prev => ({
                        ...prev,
                        start_date: mergeTime(data.start_date, time),
                      }))
                    }
                  }}
                />
              </div>
              <div className="max-w-sm">
                <label className="block font-light text-lg mb-1">
                  End Time
                </label>
                <TimePicker
                  className="text-sm"
                  date={endTime}
                  onChange={time => {
                    setEndTime(time)

                    setErrors(prev => ({
                      ...prev,
                      start_time: null,
                    }))

                    if (!data.end_date) {
                      return
                    }

                    let end_date

                    if (data.multiday) {
                      end_date = mergeTime(data.end_date, time)
                    } else if (time.getHours() >= 12 ) {
                      end_date = mergeTime(data.start_date, time)
                    } else {
                      end_date = mergeTime(addTime(data.start_date, { days: 1 }), time)
                    }

                    setData(prev => ({
                      ...prev,
                      end_date,
                    }))
                  }}
                />
              </div>
            </div>
            {errors?.start_time && (
              <LeadError message={errors.start_time} />
            )}
          </div>
        }
      </div>
    </div>
  )
}

function Description() {
  return (
    <p className='pt-4 px-8 text-lg font-light'>Haven’t locked in a date yet? That’s ok, click Next to skip this step.</p>
  )
}